<template>
  <v-sheet>
    <v-slide-x-reverse-transition hide-on-leave>
      <v-data-table
        v-if="isListMode"
        key="table"
        :headers="computedHeaders"
        :items="items"
        :options.sync="options"
        :server-items-length="totalItems"
        :loading="loadingTable"
        :items-per-page="itemsPerPage"
        :footer-props="footerProps"
        :item-key="itemIdKey"
        :no-data-text="noDataText"
        :hide-default-footer="loadingTable"
        :disable-filtering="true"
        :sort-by="sortBy"
        :sort-desc="sortDesc"
        loading-text="Carregando dados..."
        @click:row="$emit('click:row', $event)"
      >
        <template #top>
          <v-card :disabled="loadingTable" elevation="0" v-if="!hideTop">
            <v-card-title v-if="!!title" class="pb-0">{{ title }}</v-card-title>
            <v-card-text class="d-flex align-center px-4 pt-2 pb-8 flex-wrap">
              <v-text-field
                v-if="minSearchLength && minSearchLength > 0"
                v-model="search"
                placeholder="Digite para filtrar os resultados"
                class="flex-grow-1 mr-8"
                clearable
                persistent-hint
                :hint="minSearchLengthHint"
              />
              <v-switch v-if="restorable || showRemovedOption" :label="showRemovedText" v-model="withDeleted" />
              <v-switch v-else-if="customFilter" :label="customFilter.label" v-model="customFilterSwitch" />
              <v-spacer />

              <v-btn color="primary" icon title="Atualizar dados" @click="fetchItems" class="mr-4" v-if="!hideRefresh">
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
              <v-btn
                :id="newButtonLabelId"
                v-if="hasCreateAction"
                color="primary"
                :disabled="!canCreate"
                title="Criar novo"
                @click="startCreate"
                >{{ newButtonLabel }}
                <v-icon class="ml-1">mdi-plus</v-icon>
              </v-btn>
            </v-card-text>
          </v-card>
        </template>

        <template v-for="header in dataHeaders" #[`item.${header.value}`]="props">
          <slot :name="header.value" v-bind="props">
            {{ itemToValue(props.item, header) }}
          </slot>
        </template>

        <template #[`item.actions`]="{ item }" v-if="hasActionsColumn">
          <slot
            name="actions-col"
            v-bind="{
              item,
              customActions: [...customActions],
              editAction: {
                name: 'edit',
                icon: 'mdi-pencil',
                title: 'Editar',
                disabled: typeof canEdit === 'function' ? !canEdit(item) : !canEdit,
              },

              emitAction,
              startEdit,
            }"
          >
            <v-card class="d-flex justify-space-around" elevation="0" :disabled="loadingTable" color="transparent">
              <v-btn
                v-if="editable && hasItemDefaultActions"
                small
                icon
                title="Editar"
                :disabled="typeof canEdit === 'function' ? !canEdit(item) : !canEdit"
                @click="startEdit(item)"
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
              <v-btn
                v-if="removable && item[itemActiveKey] && hasItemDefaultActions"
                small
                icon
                title="Remover"
                :disabled="typeof canRemove === 'function' ? !canRemove(item) : !canRemove"
                @click="remove(item)"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
              <v-btn
                v-else-if="restorable && !item[itemActiveKey] && hasItemDefaultActions"
                small
                icon
                title="Restaurar"
                :disabled="typeof canRestore === 'function' ? !canRestore(item) : !canRestore"
                @click="restore(item)"
              >
                <v-icon>mdi-delete-restore</v-icon>
              </v-btn>
              <v-btn
                small
                icon
                v-for="customAction in customActions"
                :key="`custom-action-${customAction.name}`"
                :disabled="
                  typeof customAction.disabled === 'function' ? customAction.disabled(item) : !!customAction.disabled
                "
                :title="customAction.title"
                @click.stop="emitAction(customAction.name, item)"
                :class="customAction.customClass ? customAction.customClass(item) : ''"
              >
                <v-icon>{{ customAction.icon }}</v-icon>
              </v-btn>
            </v-card>
          </slot>
        </template>
        <template #[`footer.prepend`]>
          <slot name="footer.prepend"></slot>
        </template>
      </v-data-table>

      <v-form v-else key="form" ref="form" class="fill-height">
        <v-card
          class="fill-height d-flex flex-column overflow-y-auto"
          :disabled="loadingTable"
          :loading="loadingTable"
          elevation="0"
        >
          <v-container fluid class="overflow-y-auto">
            <v-form :disabled="readonlyMode" v-if="isCreateMode || !$scopedSlots['update-form']">
              <slot name="create-form" v-bind="{ editMode: isUpdateMode, model, original }"></slot>
            </v-form>
            <slot v-else name="update-form" v-bind="{ editMode: isUpdateMode, model, original }"></slot>
            <slot name="editable-form"></slot>
          </v-container>
          <v-spacer />
          <v-card-actions class="py-4">
            <slot name="left-custom-form-buttons"></slot>
            <v-spacer />
            <v-btn text color="primary" @click="backToListModeAndReset" class="text-none">Cancelar</v-btn>
            <slot name="custom-form-buttons"></slot>
            <v-btn v-if="isCreateMode && !hideSave" color="primary" @click="create">criar</v-btn>
            <v-btn v-else-if="isUpdateMode && !hideSave" color="primary" @click="update">atualizar</v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-slide-x-reverse-transition>
  </v-sheet>
</template>

<script>
const [LIST_MODE, UPDATE_MODE, CREATE_MODE] = [Symbol('LIST'), Symbol('UPDATE'), Symbol('CREATE')]

export default {
  props: {
    hideSave: Boolean,
    /**
     * Chave do objeto que identifica seu ID
     */
    itemIdKey: {
      type: String,
      default: 'id',
    },
    /**
     * Traz todos os resultados e filtra no front
     */
    filterDeleted: {
      type: Boolean,
      default: false,
    },
    /**
     * Chave do objeto que identifica se ele se encontra ativo
     */
    itemActiveKey: {
      type: String,
      default: 'active',
    },
    /**
     * text: string,
     * value: string,
     * align?: 'start' | 'center' | 'end',
     * sortable?: boolean,
     * width?: string | number,
     * displayConverter?: function
     */
    headers: {
      type: Array,
      default: () => [],
    },
    /**
     * name: string,
     * icon: string
     * title?: string,
     * disabled?: boolean | function
     *
     * Eventos são emitidos no padrão 'action:name'
     */
    customActions: {
      type: Array,
      default: () => [],
    },
    /**
     * texto de cabeçalho da coluna de actions
     */
    actionsHeaderText: String,
    /**
     * Rótulo do botão de criar novo item
     */
    newButtonLabel: {
      type: String,
      default: 'Novo item',
    },

    newButtonLabelId: {
      type: String,
    },
    /**
     * Número de linhas por página
     */
    itemsPerPage: {
      type: Number,
      default: 10,
    },
    /**
     * Texto exibido quando não há linhas para serem mostradas
     */
    noDataText: {
      type: String,
      default: 'Nenhum resultado encontrado',
    },
    /**
     * Opções de rodapé. Ex.:
     * 'page-text': '{0}-{1} de {2}'
     * 'items-per-page-text': 'Itens por página'
     * 'items-per-page-all-text': 'Todos'
     * 'items-per-page-options': [5, 10, 25, 50]
     */
    footerProps: {
      type: Object,
      default: () => ({
        'page-text': '{0}-{1} de {2}',
        'items-per-page-text': 'Itens por página',
        'items-per-page-all-text': 'Todos',
        'items-per-page-options': [5, 10, 25, 50],
      }),
    },
    /**
     * Indica se está liberado ou não para criar novos itens
     */
    canCreate: {
      type: Boolean,
    },
    /**
     * Indica se está liberado ou não para editar itens;
     * se for uma função, recebe o item para pode se verificar
     * a permissão.
     */
    canEdit: {
      type: [Function, Boolean],
    },
    /**
     * Indica se está liberado ou não para remover itens;
     * se for uma função, recebe o item para pode se verificar
     * a permissão.
     */
    canRemove: {
      type: [Function, Boolean],
    },
    /**
     * Indica se está liberado ou não para restaurar itens;
     * se for uma função, recebe o item para pode se verificar
     * a permissão.
     */
    canRestore: {
      type: [Function, Boolean],
    },
    /**
     * Indica se a opção de restaurar existe ou se se trata
     * de item permanentemente removível.
     */
    restorable: {
      type: Boolean,
    },
    /**
     * Exibe a opção de mostrar ou não os removidos
     * mesmo quando a opção restorable for false
     */
    showRemovedOption: Boolean,
    /**
     * Texto a ser exibido na opção de exibir ou não removidos
     */
    showRemovedText: {
      type: String,
      default: 'Exibir removidos',
    },
    /**
     * Indica se a opção de editar existe ou se se trata
     * de item não editável.
     */
    editable: {
      type: Boolean,
    },
    /**
     * Indica se a opção de remover existe ou se se trata
     * de item não removível.
     */
    removable: {
      type: Boolean,
    },
    /**
     * Número mínimo de caracteres no campo de filtro
     * para se fazer uma consulta no servidor
     * Se menor ou igual a zero, o campo não é exibido.
     */
    minSearchLength: {
      type: Number,
      default: 3,
    },
    /**
     * Função responsável por receber os dados do item selecionado
     * (selectedItem) e usá-lo para preencher os campos através de
     * formModel
     */
    fillUpdateForm: {
      type: Function,
      default: (formModel, selectedItem) => undefined,
    },
    /**
     * Função geradora do objeto que será preenchido com os dados
     * do form e enviado ao backend para INSERT ou UPDATE
     */
    modelGenerator: {
      type: Function,
      default: () => ({}),
    },
    /**
     * Coloca a tabela em modo apenas listagem, desaparecendo
     * com os botões de ação e criação
     */
    listOnly: Boolean,
    /**
     * Coloca a tabela em modo apenas listagem, desaparecendo
     * com os botões de ação e criação, mas mantendo os botões
     * de ação custom fornecidos
     */
    customOnly: Boolean,
    /**
     * Título opcional a ser exibido no canto superior esquerdo
     * da tabela (ao lado do campo de filtragem de texto, se houver)
     */
    title: String,

    sortBy: [String, Array],
    sortDesc: [Boolean, Array],
    createAction: Function,
    updateAction: Function,
    removeAction: Function,
    restoreAction: Function,
    fetchAction: {
      type: Function,
      default: (options) => ({ total: 0, items: [] }),
    },
    hideRefresh: {
      type: Boolean,
      default: false,
    },
    hideTop: {
      type: Boolean,
      default: false,
    },
    customFilter: {
      type: Object,
    },
    readonlyMode: {
      type: Boolean,
      default: false,
    },

    actionCustomWidth: Number,
  },
  computed: {
    hasCreateAction() {
      return !this.listOnly && !this.customOnly
    },
    hasItemDefaultActions() {
      return !this.listOnly && !this.customOnly && (this.editable || this.removable || this.restorable)
    },
    hasCustomActions() {
      return !this.listOnly && this.customActions.length > 0
    },
    hasActionsColumn() {
      return this.hasItemDefaultActions || this.hasCustomActions
    },
    actionsColumnLength() {
      let width = this.customActions.length * 48
      if (this.hasItemDefaultActions) {
        if (this.editable) {
          width += 48
        }
        if (this.removable || this.restorable) {
          width += 48
        }
      }

      return this.actionCustomWidth ?? width
    },
    computedHeaders() {
      const headers = [...this.headers]
      if (this.hasActionsColumn) {
        headers.push({
          value: 'actions',
          width: this.actionsColumnLength,
          sortable: false,
          text: this.actionsHeaderText,
        })
      }
      return headers
    },
    minSearchLengthHint() {
      if (!this.minSearchLength) {
        return ''
      }

      const characterWord = this.minSearchLength === 1 ? 'caractere' : 'caracteres'
      return `Digite ao menos ${this.minSearchLength} ${characterWord} para filtrar`
    },
    dataHeaders() {
      return this.headers.filter((header) => !!header.text)
    },
    isCreateMode() {
      return this.mode === CREATE_MODE
    },
    isUpdateMode() {
      return this.mode === UPDATE_MODE
    },
    isListMode() {
      return this.mode === LIST_MODE
    },
  },
  watch: {
    /**
     * Acaba chamando o fetchItems a primeira vez
     * que o componente é montado por causa da opção
     * .sync usado no options do v-data-table
     */
    options: {
      deep: true,
      immediate: false,
      handler() {
        this.fetchItems()
      },
    },
    /**
     * Acaba chamando o fetchItems por intermédio do watch de options,
     * quando reseta o options.page para 1
     */
    search(newValue) {
      const value = newValue ? newValue.trim() : ''
      if (value.length === 0 || value.length >= this.minSearchLength) {
        if (this.options.page !== 1) {
          this.options.page = 1
        } else {
          this.fetchItems()
        }
      }
    },
    /**
     * Acaba chamando o fetchItems por intermédio do watch de options,
     * quando reseta o options.page para 1
     */
    withDeleted() {
      if (this.options.page !== 1) {
        this.options.page = 1
      } else {
        this.fetchItems()
      }
    },
    customFilterSwitch() {
      if (this.options.page !== 1) {
        this.options.page = 1
      } else {
        this.fetchItems()
      }
    },
  },
  data() {
    return {
      mode: LIST_MODE,
      loadingTable: false,
      totalItems: 0,
      search: '',
      withDeleted: false,
      customFilterSwitch: false,
      items: [],
      /**
       * "page": 1,
       * "itemsPerPage": 10,
       * "sortBy": [ "calories" ],
       * "sortDesc": [ false ]
       */
      options: {},
      model: null,
      original: null,
    }
  },
  methods: {
    resetValidation() {
      if (this.$refs.form) {
        this.$refs.form.resetValidation()
      }
    },
    itemToValue(item, header) {
      return header.displayConverter ? header.displayConverter(item) : this.$util.deepObjectValue(item, header.value)
    },
    emitAction(actionName, item) {
      this.$emit(`action:${actionName}`, item)
    },
    async fetchItems() {
      try {
        this.loadingTable = true
        const result = await this.fetchAction({ ...this.options, search: this.search, withDeleted: this.withDeleted })
        if (this.filterDeleted && !this.withDeleted) {
          result.items = result.items.filter((item) => item.active)
        }
        if (this.customFilterSwitch && this.customFilter.rule) {
          result.items = this.customFilter.rule(result.items)
        }
        this.totalItems = result.total
        this.items = result.items
      } catch (error) {
        this.$root.error(error)
      } finally {
        this.loadingTable = false
      }
    },
    async startCreate() {
      this.mode = CREATE_MODE
      this.original = null
      this.$emit('mode:create')
    },
    updateOriginal(item) {
      this.original = this.$util.copy(item)
    },
    async startEdit(item) {
      this.mode = UPDATE_MODE
      this.$emit('mode:edit', item)
    },
    async backToListModeAndReset() {
      if (this.$refs.form) {
        this.$refs.form.reset()
      }
      this.model = null
      this.original = null
      this.mode = LIST_MODE
      this.$emit('mode:list')
    },

    async remove(item) {
      if (!this.removeAction) {
        this.$root.warn('removeAction not provided for crud-table')
        return
      }

      try {
        this.loadingTable = true
        const result = await this.removeAction(item)
        if (result) {
          await this.fetchItems()
          this.emitAction('remove', result)
        }
      } finally {
        this.loadingTable = false
      }
    },
    async restore(item) {
      if (!this.restoreAction) {
        this.$root.warn('restoreAction not provided for crud-table')
        return
      }

      try {
        this.loadingTable = true
        const result = await this.restoreAction(item)
        if (result) {
          await this.fetchItems()
          this.emitAction('restore', result)
        }
      } finally {
        this.loadingTable = false
      }
    },
    async create() {
      if (!this.createAction) {
        this.$root.warn('createAction not provided for crud-table')
        return
      }

      if (!this.$refs.form.validate()) {
        return
      }

      try {
        this.loadingTable = true
        const result = await this.createAction(this.model)
        if (result) {
          await this.fetchItems()
          this.emitAction('create', result)
        }
      } finally {
        this.mode = LIST_MODE
        this.loadingTable = false
      }
    },
    async update() {
      if (!this.updateAction) {
        this.$root.warn('updateAction not provided for crud-table')
        return
      }

      if (!this.$refs.form.validate()) {
        return
      }

      try {
        this.loadingTable = true

        if (!this.model) {
          await this.updateAction(null, null)
        } else {
          const { [this.itemIdKey]: key, ...restModel } = this.model
          const result = await this.updateAction(key, restModel)
          if (result) {
            await this.fetchItems()
            this.emitAction('update', result)
          }
        }
      } finally {
        this.loadingTable = false
        this.mode = LIST_MODE
      }
    },
    refresh(returnToFirstPage = false) {
      if (returnToFirstPage) {
        this.options.page = 1
      }

      this.fetchItems()
    },
  },
}
</script>
